"use client";

import {
  type MotionValue,
  useMotionValue,
  useScroll,
  useTransform,
} from "motion/react";
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

// =============================================================================

const FooterProgressContext = createContext<MotionValue<number> | null>(null);

/**
 * Reveal progress of the footer: 0 when its top edge enters the viewport,
 * 1 once it is fully uncovered.
 */
export function useFooterProgress(): MotionValue<number> {
  const progress = useContext(FooterProgressContext);
  if (!progress)
    throw new Error("useFooterProgress must be used in FooterReveal");
  return progress;
}

type FooterRevealProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Sticky footer reveal (Olivier Larose's "sticky footer", method 2): the
 * footer sits still under the page, which slides away to uncover it.
 * - the outer box reserves the footer height in the flow and clips it
 * - the middle box is one viewport taller and pulled up by one viewport
 * - the content sticks to the bottom of the viewport inside it
 * The footer is at least one viewport tall. When its content is taller than
 * the screen (small or landscape phones), the sticky reveal would hide its
 * top: it then simply follows the page.
 */
export function FooterReveal({ children, className }: FooterRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isStatic, setIsStatic] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end end"],
  });
  // A function transform opts out of motion's native ScrollTimeline
  // acceleration, which mistracks this sticky + clip-path structure
  const scrollProgress = useTransform(scrollYProgress, (value) => value);
  // Following the page, the footer is not uncovered: no reveal animation
  const uncovered = useMotionValue(1);
  const progress = isStatic ? uncovered : scrollProgress;

  useEffect(() => {
    const root = ref.current;
    const content = root?.querySelector("[data-footer-content]")
      ?.firstElementChild as HTMLElement | null | undefined;
    if (!root || !content) return;
    const update = () => {
      const height = Math.ceil(content.offsetHeight);
      root.style.setProperty("--footer-content-h", `${height}px`);
      setIsStatic(height > window.innerHeight);
    };
    const observer = new ResizeObserver(update);
    observer.observe(content);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-static={isStatic || undefined}
      className={cn(
        "group/footer relative h-(--footer-h) data-[static]:h-auto",
        className
      )}
      style={
        {
          "--footer-h": "max(100dvh, var(--footer-content-h, 40rem))",
          clipPath: "polygon(0% 0, 100% 0%, 100% 100%, 0 100%)",
        } as CSSProperties
      }
    >
      <div className="relative -top-[100dvh] h-[calc(100dvh+var(--footer-h))] group-data-[static]/footer:top-0 group-data-[static]/footer:h-auto">
        <div className="sticky top-[calc(100dvh-var(--footer-h))] h-(--footer-h) group-data-[static]/footer:static group-data-[static]/footer:h-auto">
          <FooterProgressContext value={progress}>
            {children}
          </FooterProgressContext>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
